import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const statusLabels = {
  active: "Aktif çalışma",
  waiting_next_day: "Gece yarısını bekliyor",
  resting: "7 günlük ara",
  locked: "Deneyim kaydı bekliyor",
  frozen: "Donduruldu",
  completed: "Tamamlandı",
  materializing: "Hazırlanıyor",
};

export default function AdminDailyProgramPanel({ journey, refresh }) {
  const program = journey?.daily_program;
  const runId = program?.run?.id;
  const [catalog, setCatalog] = useState([]);
  const [overrideId, setOverrideId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [remoteSessionDays, setRemoteSessionDays] = useState(0);

  useEffect(() => {
    if (!runId) return;
    api
      .get("/admin/daily-program/practice-catalog")
      .then((response) => setCatalog(response.data?.items || []))
      .catch(() => setCatalog([]));
  }, [runId]);

  useEffect(() => {
    setRemoteSessionDays(
      Number(
        program?.run?.queued_plan?.remote_session_days
          ?? program?.run?.scheduled_break?.remote_session_days
          ?? program?.run?.remote_session_days
          ?? 0,
      ),
    );
  }, [
    runId,
    program?.run?.queued_plan?.remote_session_days,
    program?.run?.scheduled_break?.remote_session_days,
    program?.run?.remote_session_days,
  ]);

  if (!program?.run) return null;
  const { run, steps = [], experiences = [] } = program;
  const queuedPlan = run.queued_plan?.status === "queued" ? run.queued_plan : null;
  const scheduledBreak = run.scheduled_break?.status === "scheduled" ? run.scheduled_break : null;
  const transitionPlan = queuedPlan || scheduledBreak;
  const activePlanTarget = Number(
    run.active_plan_target_steps || queuedPlan?.starts_after_ordinal || run.target_completed_steps || 0,
  );
  const activeRemaining = Math.max(0, activePlanTarget - Number(run.completed_steps || 0));
  const phaseRows = [...new Map(steps.map((step) => [step.phase_number, step])).values()];
  const prepared = run.phase_selections?.[String(run.phase_number)];
  const overrideItems = catalog.filter(
    (item) => item.kind === prepared?.kind && item.status === "approved",
  );

  const updateState = async (action) => {
    setSaving(true);
    setError("");
    try {
      await api.patch(`/admin/daily-programs/${run.id}/state`, { action });
      toast.success(action === "freeze" ? "Program donduruldu." : "Program yeniden açıldı.");
      await refresh?.();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setSaving(false);
    }
  };

  const archiveExperience = async (experienceId) => {
    if (!globalThis.confirm?.("Bu deneyim arşivlensin mi?")) return;
    setSaving(true);
    setError("");
    try {
      await api.patch(`/admin/daily-programs/${run.id}/experiences/${experienceId}/archive`);
      toast.success("Deneyim arşivlendi.");
      await refresh?.();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setSaving(false);
    }
  };

  const overridePhase = async () => {
    if (!overrideId) return;
    setSaving(true);
    setError("");
    try {
      await api.patch(`/admin/daily-programs/${run.id}/phases/${run.phase_number}/override`, {
        catalog_item_id: overrideId,
      });
      toast.success("Yeni faz çalışması güncellendi.");
      setOverrideId("");
      await refresh?.();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setSaving(false);
    }
  };

  const saveRemoteSession = async () => {
    setSaving(true);
    setError("");
    try {
      await api.patch(`/admin/daily-programs/${run.id}/remote-session`, {
        days: Number(remoteSessionDays),
        expected_version: run.version,
      });
      toast.success(
        Number(remoteSessionDays)
          ? `${remoteSessionDays} gün uzaktan seans hakkı tanımlandı.`
          : "Uzaktan seans hakkı kaldırıldı.",
      );
      await refresh?.();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="border border-pleiades-border bg-white p-4 mb-4">
      {error && (
        <div
          role="alert"
          className="mb-4 p-3 border border-red-200 bg-red-50 text-red-700 text-[12px]"
        >
          {error}
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-pleiades-muted">
            Günlük program motoru
          </div>
          <h2 className="text-[14px] font-semibold mt-1">
            {statusLabels[run.status] || run.status}
          </h2>
          <p className="mt-2 text-[12px] leading-[1.6] text-pleiades-slate">
            Kullanıcı yalnız bugünün çalışmasını görür. Aşağıdaki iskelet yönetici görünümüdür.
          </p>
        </div>
        <span className="h-fit px-3 py-2 border border-pleiades-border bg-pleiades-sidebar/50 text-[11px]">
          {run.completed_steps || 0} / {run.target_completed_steps || 0} tamamlandı
        </span>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {run.status === "frozen" ? (
          <button
            type="button"
            disabled={saving}
            onClick={() => updateState("resume")}
            className="min-h-10 px-4 border border-pleiades-blue text-pleiades-blue text-[12px] disabled:opacity-50"
          >
            Programı devam ettir
          </button>
        ) : !["completed", "materializing"].includes(run.status) ? (
          <button
            type="button"
            disabled={saving}
            onClick={() => updateState("freeze")}
            className="min-h-10 px-4 border border-amber-300 text-amber-800 text-[12px] disabled:opacity-50"
          >
            Programı dondur
          </button>
        ) : null}
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3" aria-label="Çalışma planı sırası">
        <article className="border border-pleiades-border bg-white p-3">
          <div className="text-[10px] uppercase tracking-wider text-pleiades-muted">Aktif çalışma</div>
          <strong className="mt-2 block text-[13px] text-pleiades-ink">
            {activeRemaining} çalışma kaldı
          </strong>
          <p className="mt-1 text-[11px] leading-5 text-pleiades-slate">
            Başlamış ve tamamlanmış günler değiştirilemez.
          </p>
        </article>
        <article className="border border-pleiades-border bg-pleiades-sidebar/30 p-3">
          <div className="text-[10px] uppercase tracking-wider text-pleiades-muted">Sıradaki çalışma</div>
          <strong className="mt-2 block text-[13px] text-pleiades-ink">
            {queuedPlan ? `${queuedPlan.total_days} günlük plan hazır` : "Henüz hazırlanmadı"}
          </strong>
          <p className="mt-1 text-[11px] leading-5 text-pleiades-slate">
            {queuedPlan
              ? "Mevcut çalışma tamamlanınca otomatik sıraya girecek."
              : activeRemaining <= 5
                ? "Plan bitişe yaklaştı; GPT üzerinden sıradaki plan hazırlanabilir."
                : "Son 5 çalışma kaldığında hazırlık uyarısı oluşur."}
          </p>
        </article>
        <article className="border border-pleiades-border bg-pleiades-sidebar/30 p-3">
          <div className="text-[10px] uppercase tracking-wider text-pleiades-muted">Ara ve uzaktan seans</div>
          <strong className="mt-2 block text-[13px] text-pleiades-ink">
            {transitionPlan?.rest_days ? `${transitionPlan.rest_days} gün dinlenme` : "Ara planlanmadı"}
          </strong>
          <p className="mt-1 text-[11px] leading-5 text-pleiades-slate">
            {transitionPlan?.remote_session_days
              ? `${transitionPlan.remote_session_days} gün uzaktan seans hakkı tanımlı.`
              : "Uzaktan seans hakkı tanımlı değil."}
          </p>
        </article>
      </div>

      {!["completed", "materializing"].includes(run.status) && (
        <div className="mt-4 border border-pleiades-border bg-pleiades-sidebar/40 p-4">
          <div className="text-[10px] uppercase tracking-wider text-pleiades-muted">
            {transitionPlan?.rest_days
              ? `Yaklaşan ${transitionPlan.rest_days} günlük ara · uzaktan seans`
              : "Yaklaşan ara · uzaktan seans"}
          </div>
          <p className="mt-2 text-[12px] leading-[1.6] text-pleiades-slate">
            Tarih ve saat burada belirlenmez. Kullanıcı, ara döneminde danışmanıyla görüşerek
            randevu alır.
          </p>
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <select
              value={remoteSessionDays}
              onChange={(event) => setRemoteSessionDays(Number(event.target.value))}
              className="min-h-10 flex-1 border border-pleiades-border bg-white px-3 text-[12px]"
              aria-label="Uzaktan seans günü"
            >
              <option value={0}>Uzaktan seans yok</option>
              <option value={1}>1 gün uzaktan seans</option>
              <option value={2}>2 gün uzaktan seans</option>
            </select>
            <button
              type="button"
              disabled={
                saving
                || (run.planning_mode === "guided" && !transitionPlan)
                || Number(remoteSessionDays) === Number(
                  transitionPlan?.remote_session_days ?? run.remote_session_days ?? 0,
                )
              }
              onClick={saveRemoteSession}
              className="min-h-10 px-4 bg-pleiades-blue text-white text-[12px] disabled:opacity-40"
            >
              Seans hakkını kaydet
            </button>
          </div>
          {Number(transitionPlan?.remote_session_days ?? run.remote_session_days ?? 0) > 0 && (
            <p className="mt-3 text-[11px] font-medium text-pleiades-blue">
              Kayıtlı hak: {transitionPlan?.remote_session_days ?? run.remote_session_days} gün
              {transitionPlan ? " · sıradaki dinlenme dönemi" : ` · ${run.remote_session_phase_number}. fazın ara dönemi`}
            </p>
          )}
        </div>
      )}

      {run.status === "resting" && prepared && (
        <div className="mt-4 border border-pleiades-border bg-pleiades-sidebar/40 p-4">
          <div className="text-[10px] uppercase tracking-wider text-pleiades-muted">
            Ara döneminde hazırlanmış yeni faz
          </div>
          <p className="mt-2 text-[12px]">
            {prepared.name} · {prepared.meditation_code}
          </p>
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <select
              value={overrideId}
              onChange={(event) => setOverrideId(event.target.value)}
              className="min-h-10 flex-1 border border-pleiades-border bg-white px-3 text-[12px]"
            >
              <option value="">Onaylı havuzdan başka çalışma seç</option>
              {overrideItems.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={!overrideId || saving}
              onClick={overridePhase}
              className="min-h-10 px-4 bg-pleiades-blue text-white text-[12px] disabled:opacity-40"
            >
              Seçimi değiştir
            </button>
          </div>
        </div>
      )}

      <dl className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3 text-[11px]">
        <div>
          <dt className="text-pleiades-muted">Paket</dt>
          <dd className="mt-1 font-semibold">{run.package_months} ay</dd>
        </div>
        <div>
          <dt className="text-pleiades-muted">Faz</dt>
          <dd className="mt-1 font-semibold">{run.phase_number}</dd>
        </div>
        <div>
          <dt className="text-pleiades-muted">Kaçırılan</dt>
          <dd className="mt-1 font-semibold">{run.missed_days || 0} gün</dd>
        </div>
        <div>
          <dt className="text-pleiades-muted">Uzatma</dt>
          <dd className="mt-1 font-semibold">{run.extension_days || 0} gün</dd>
        </div>
        <div>
          <dt className="text-pleiades-muted">Deneyimsiz seri</dt>
          <dd className="mt-1 font-semibold">{run.no_experience_streak || 0} / 3</dd>
        </div>
      </dl>

      <details className="mt-4 border border-pleiades-border">
        <summary className="min-h-11 px-4 flex items-center cursor-pointer text-[12px] font-medium">
          Program fazları ve seçimler
        </summary>
        <div className="border-t border-pleiades-border divide-y divide-pleiades-border">
          {phaseRows.map((step) => {
            const details = step.practice?.selection_details;
            return (
              <div key={step.phase_number} className="px-4 py-3 text-[11px]">
                <div className="grid sm:grid-cols-[70px_100px_1fr] gap-2">
                  <span className="font-semibold">{step.phase_number}. faz</span>
                  <span className="text-pleiades-muted">
                    {step.practice_kind === "mantra" ? "Mantra" : "Esmâ"}
                  </span>
                  <span>
                    {step.practice?.name
                      ? `${step.practice.name} · ${step.practice.meditation_code}`
                      : "Faz başlamadan önce seçilecek"}
                  </span>
                </div>
                {details && (
                  <div className="mt-3 sm:ml-[170px] border-l-2 border-pleiades-gold/40 pl-3 text-pleiades-slate">
                    <p>
                      <span className="font-semibold">İç seçim kaydı:</span> {details.primary_topic}
                      {details.secondary_topic ? ` · ${details.secondary_topic}` : ""}
                    </p>
                    {details.admin_summary && <p className="mt-1">{details.admin_summary}</p>}
                    {(details.evidence_quotes || []).length > 0 && (
                      <ul className="mt-2 space-y-1 text-pleiades-muted">
                        {details.evidence_quotes.map((quote, index) => (
                          <li key={`${step.phase_number}-${index}`}>“{quote}”</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </details>

      <details className="mt-3 border border-pleiades-border">
        <summary className="min-h-11 px-4 flex items-center cursor-pointer text-[12px] font-medium">
          Deneyim kayıtları ({experiences.length})
        </summary>
        <div className="border-t border-pleiades-border divide-y divide-pleiades-border">
          {experiences.length === 0 ? (
            <p className="p-4 text-[11px] text-pleiades-muted">Henüz deneyim kaydı yok.</p>
          ) : (
            experiences.map((entry) => (
              <blockquote
                key={entry.id}
                className="p-4 text-[12px] leading-[1.65] text-pleiades-slate"
              >
                “{entry.body}”
                <footer className="mt-2 flex items-center justify-between gap-3 text-[10px] text-pleiades-muted">
                  <span>{entry.local_day}</span>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => archiveExperience(entry.id)}
                    className="text-red-700 underline underline-offset-2 disabled:opacity-40"
                  >
                    Arşivle
                  </button>
                </footer>
              </blockquote>
            ))
          )}
        </div>
      </details>
    </section>
  );
}
